import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const useCases = [
  {
    num: '01',
    icon: 'ri-download-cloud-line',
    title: 'Patch Süreçlerinin Otomatikleştirilmesi',
    tag: 'Endpoint Central',
    tagColor: '#F97316',
    desc: 'Otomatik patch yönetimi ile sunucu ve istemci sistemlerinizi her zaman güncel ve güvende tutun.',
    metric: '%95 güvenlik açığı azalması',
    metricIcon: 'ri-shield-check-line',
    image: 'https://public.readdy.ai/ai/img_res/ce84285add7b9a53641920b9726b25a5.jpg',
  },
  {
    num: '02',
    icon: 'ri-speed-up-line',
    title: "ITSM Talep Yönetiminde %40 Hızlanma",
    tag: 'ServiceDesk Plus',
    tagColor: '#0891B2',
    desc: 'Akıllı ticket yönlendirme ve otomasyon kurallarıyla destek süreçlerinizi hızlandırın.',
    metric: '%40 daha hızlı çözüm',
    metricIcon: 'ri-timer-flash-line',
    image: 'https://readdy.ai/api/search-image?query=IT%20service%20desk%20ticketing%20system%20dashboard%20on%20bright%20office%20monitor%20showing%20ticket%20queue%20management%20interface%20with%20priority%20color%20coding%2C%20agent%20workload%20distribution%20chart%2C%20SLA%20timer%20countdown%20indicators%2C%20customer%20satisfaction%20score%20widget%2C%20clean%20modern%20helpdesk%20software%20UI%2C%20white%20light%20gray%20design%20professional%20enterprise%20support%20platform&width=560&height=360&seq=uc02-light&orientation=landscape',
  },
  {
    num: '03',
    icon: 'ri-shield-check-line',
    title: 'Active Directory Süreçlerinin Otomasyonu',
    tag: 'ADManager + AutomMate',
    tagColor: '#F97316',
    desc: 'Kullanıcı oluşturma, silme ve erişim güncellemelerini otomatikleştirin, manuel hataları ortadan kaldırın.',
    metric: '%80 manuel işlem azalması',
    metricIcon: 'ri-robot-line',
    image: 'https://readdy.ai/api/search-image?query=Active%20Directory%20automation%20workflow%20diagram%20on%20bright%20professional%20monitor%20showing%20automated%20user%20provisioning%20flow%20chart%2C%20bulk%20user%20management%20interface%2C%20role-based%20access%20control%20configuration%20screen%2C%20automation%20rule%20builder%20UI%20panel%2C%20clean%20white%20background%20professional%20enterprise%20identity%20management%20software%2C%20modern%20light%20interface%20design&width=560&height=360&seq=uc03-light&orientation=landscape',
  },
  {
    num: '04',
    icon: 'ri-flow-chart',
    title: 'Zoho CRM + ManageEngine Entegrasyonu',
    tag: 'Zoho ONE + ManageEngine',
    tagColor: '#0891B2',
    desc: 'Satış, servis ve IT operasyonlarını tek iş akışında birleştirerek veri tutarlılığını sağlayın.',
    metric: 'Tek entegre platform',
    metricIcon: 'ri-links-line',
    image: 'https://readdy.ai/api/search-image?query=Business%20process%20integration%20dashboard%20showing%20CRM%20and%20IT%20service%20management%20unified%20workflow%20on%20bright%20screen%2C%20customer%20ticket%20linked%20to%20IT%20asset%20management%2C%20data%20flow%20visualization%20between%20Zoho%20CRM%20and%20ManageEngine%2C%20integration%20mapping%20diagram%20with%20connected%20data%20points%2C%20clean%20modern%20white%20interface%20enterprise%20software%20integration%20platform&width=560&height=360&seq=uc04-light&orientation=landscape',
  },
  {
    num: '05',
    icon: 'ri-eye-line',
    title: 'IT Operasyonlarında Proaktif İzleme',
    tag: 'ManageEngine Monitoring',
    tagColor: '#F97316',
    desc: 'Altyapı sorunlarını oluşmadan önce tespit edin; kesinti süresini minimize edin.',
    metric: '%99.9 uptime hedefi',
    metricIcon: 'ri-pulse-line',
    image: 'https://readdy.ai/api/search-image?query=Real-time%20IT%20infrastructure%20monitoring%20operations%20center%20dashboard%20on%20bright%20monitor%20showing%20server%20health%20metrics%20live%20graphs%2C%20network%20topology%20map%20all%20nodes%20green%20status%2C%20alert%20management%20panel%20with%20resolved%20incidents%2C%20uptime%20percentage%20statistics%2C%20clean%20modern%20NOC%20dashboard%20white%20light%20gray%20professional%20design%2C%20enterprise%20monitoring%20software%20screenshot&width=560&height=360&seq=uc05-light&orientation=landscape',
  },
];

const UseCases = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.uc-header', { scrollTrigger: { trigger: '.uc-header', start: 'top 85%' }, opacity: 0, y: 30, duration: 0.9, ease: 'power3.out' });
      gsap.from('.uc-card', { scrollTrigger: { trigger: '.uc-list', start: 'top 70%' }, opacity: 0, y: 40, duration: 0.7, stagger: 0.12, ease: 'power3.out' });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="uc-header text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#ECFEFF] border border-[#0891B2]/30 text-[#0E7490] text-xs font-semibold mb-5 font-body">
            <i className="ri-lightbulb-line"></i>
            Kullanım Senaryoları
          </div>
          <h2 className="text-4xl font-extrabold text-[#0F172A] mb-4 font-heading leading-tight">
            Gerçek Dünya IT<br />
            <span className="text-[#0891B2]">Dönüşüm Hikayeleri</span>
          </h2>
          <p className="text-base text-[#475569] max-w-xl mx-auto font-body">
            Müşterilerimizin IT süreçlerinde yaşadığı somut iyileşmeleri keşfedin.
          </p>
        </div>

        <div className="uc-list space-y-8">
          {useCases.map((uc, i) => (
            <article
              key={i}
              className={`uc-card grid grid-cols-1 lg:grid-cols-2 gap-8 items-center p-8 rounded-2xl border border-[#E2E8F0] ${i % 2 === 0 ? 'bg-[#F8FAFC]' : 'bg-white'}`}
            >
              <div className={i % 2 === 1 ? 'order-1 lg:order-2' : ''}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm font-black text-[#CBD5E1] font-heading">{uc.num}</span>
                  <span
                    className="px-3 py-1 rounded-full text-xs font-bold font-body"
                    style={{ background: `${uc.tagColor}15`, color: uc.tagColor, border: `1px solid ${uc.tagColor}25` }}
                  >
                    {uc.tag}
                  </span>
                </div>
                <h3 className="text-xl font-extrabold text-[#0F172A] mb-3 font-heading leading-snug">{uc.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed mb-5 font-body">{uc.desc}</p>
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg w-fit" style={{ background: `${uc.tagColor}12`, border: `1px solid ${uc.tagColor}25` }}>
                  <i className={`${uc.metricIcon} text-base`} style={{ color: uc.tagColor }}></i>
                  <span className="text-sm font-bold font-body" style={{ color: uc.tagColor }}>{uc.metric}</span>
                </div>
                <a href="#contact" className="inline-flex items-center gap-2 mt-5 text-sm font-semibold cursor-pointer font-body transition-all duration-200 hover:gap-3" style={{ color: uc.tagColor }}>
                  Detaylı Bilgi Al <i className="ri-arrow-right-line"></i>
                </a>
              </div>

              <div className={`rounded-xl overflow-hidden border border-[#E2E8F0] ${i % 2 === 1 ? 'order-2 lg:order-1' : ''}`}>
                <img
                  src={uc.image}
                  alt={uc.title}
                  title={uc.title}
                  className="w-full h-52 object-cover object-top"
                />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCases;
